import { useEffect, useRef, useCallback } from "react";

import { useAtom, useAtomValue } from "../editor-jotai";
import { recordingStateAtom, recordingStatusAtom } from "../recording/atoms";
import {
  createBackgroundBlurPipeline,
  isBackgroundBlurSupported,
} from "../recording/backgroundBlurPipeline";
import { getCameraStream } from "../recording/cameraStreamHelper";
import { setCameraVideoElement } from "../recording/cameraVideoRef";
import { useDraggablePosition } from "../recording/useDraggablePosition";

import type { BackgroundBlurPipeline } from "../recording/backgroundBlurPipeline";

import "./CameraOverlay.scss";

export const CameraOverlay = () => {
  const status = useAtomValue(recordingStatusAtom);
  const [recordingState, setRecordingState] = useAtom(recordingStateAtom);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const blurPipelineRef = useRef<BackgroundBlurPipeline | null>(null);

  const cameraSize = recordingState.cameraSize;

  const defaultX =
    typeof window !== "undefined" ? window.innerWidth - cameraSize - 30 : 0;
  const defaultY =
    typeof window !== "undefined" ? window.innerHeight - cameraSize - 30 : 0;

  const initialPos =
    recordingState.cameraPosition.x >= 0
      ? recordingState.cameraPosition
      : { x: defaultX, y: defaultY };

  const { position, onPointerDown } = useDraggablePosition(initialPos);

  const { settings } = recordingState;
  const shouldHaveCamera = status !== "idle" && settings.cameraEnabled;

  useEffect(() => {
    if (!shouldHaveCamera) {
      blurPipelineRef.current?.destroy();
      blurPipelineRef.current = null;
      if (streamRef.current) {
        for (const track of streamRef.current.getTracks()) {
          track.stop();
        }
        streamRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const initCamera = async () => {
      try {
        const rawStream = await getCameraStream(settings.cameraDeviceId);

        if (cancelled) {
          for (const track of rawStream.getTracks()) {
            track.stop();
          }
          return;
        }

        streamRef.current = rawStream;
        let displayStream = rawStream;

        if (settings.backgroundBlur && isBackgroundBlurSupported()) {
          try {
            const pipeline = await createBackgroundBlurPipeline(rawStream);
            if (cancelled) {
              pipeline.destroy();
              return;
            }
            blurPipelineRef.current = pipeline;
            displayStream = pipeline.outputStream;
          } catch (err) {
            console.error("[Recording] CameraOverlay blur init failed:", err);
          }
        }

        if (videoRef.current) {
          videoRef.current.srcObject = displayStream;
          setCameraVideoElement(videoRef.current);
        }
      } catch (err) {
        console.error("[Recording] CameraOverlay init failed:", err);
      }
    };

    initCamera();

    return () => {
      cancelled = true;
      setCameraVideoElement(null);
      blurPipelineRef.current?.destroy();
      blurPipelineRef.current = null;
      if (streamRef.current) {
        for (const track of streamRef.current.getTracks()) {
          track.stop();
        }
        streamRef.current = null;
      }
    };
  }, [shouldHaveCamera, settings.cameraDeviceId, settings.backgroundBlur]);

  useEffect(() => {
    setRecordingState((prev) => ({
      ...prev,
      cameraPosition: position,
    }));
  }, [position, setRecordingState]);

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);
      const startX = e.clientX;
      const startY = e.clientY;
      const startSize = recordingState.cameraSize;

      const onMove = (ev: PointerEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        const delta = Math.max(dx, dy);
        const newSize = Math.min(400, Math.max(100, startSize + delta));
        setRecordingState((prev) => ({ ...prev, cameraSize: newSize }));
      };

      const onUp = () => {
        target.releasePointerCapture(e.pointerId);
        target.removeEventListener("pointermove", onMove);
        target.removeEventListener("pointerup", onUp);
      };

      target.addEventListener("pointermove", onMove);
      target.addEventListener("pointerup", onUp);
    },
    [recordingState.cameraSize, setRecordingState],
  );

  if (!shouldHaveCamera) {
    return null;
  }

  const isCircle = settings.cameraShape === "circle";

  return (
    <div
      className="camera-overlay"
      style={{
        left: position.x,
        top: position.y,
        width: cameraSize,
        height: cameraSize,
        borderRadius: isCircle ? "50%" : 20,
      }}
      onPointerDown={onPointerDown}
    >
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="camera-overlay__video"
        style={{
          borderRadius: isCircle ? "50%" : 20,
          transform: settings.cameraMirrored ? "scaleX(-1)" : "none",
        }}
      />
      <div
        className="camera-overlay__resize-handle"
        onPointerDown={onResizePointerDown}
      />
    </div>
  );
};
